import { inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { catchError, EMPTY, Observable, of, switchMap, tap, using } from 'rxjs';

import { Folder } from '@efficiently/models';

import { FoldersRepository } from '@efficiently/client-api-content';

import { FolderStore } from '../api-folders/folder-store';

/**
 * Internal state shape for the folder store.
 */
type FolderState = {
	folders: Folder[];
	error: string | null;
};

/**
 * Concrete implementation of {@link FolderStore}.
 *
 * Extends {@link ComponentStore} for local state management and exposes a reactive
 * `folders$` stream that automatically loads folders on first subscription.
 *
 * Consumers should inject {@link FolderStore} (abstract class),
 * not this service directly — this keeps the ComponentStore API hidden.
 */
@Injectable()
export class FolderStoreService extends ComponentStore<FolderState> implements FolderStore {
	/**
	 * Data-access layer responsible for loading folders
	 * via remote API (e.g. GraphQL).
	 */
	private readonly foldersRepository = inject(FoldersRepository);

	constructor() {
		super({
			folders: [],
			error: null,
		});
	}

	/**
	 * The last error that occurred during a load operation.
	 * Emits `null` when there is no error.
	 */
	public readonly error$ = this.select(state => state.error);

	/**
	 * Observable of folders.
	 *
	 * Uses RxJS `using` to trigger a one-time load from the repository
	 * when the first subscriber appears, and automatically cleans up
	 * the inner subscription when all subscribers unsubscribe.
	 */
	public readonly folders$: Observable<Folder[]> = using(
		() =>
			of(void 0)
				.pipe(
					tap(() => {
						this.patchState({ error: null });
					}),
					switchMap(() => {
						return this.foldersRepository.getFolders(undefined).pipe(
							tap(folders => {
								this.patchState({ folders });
							}),
							catchError(error => {
								const message = error instanceof Error ? error.message : 'Failed to load folders';
								this.patchState({ error: message });
								return EMPTY;
							}),
						);
					}),
				)
				.subscribe(),
		() => this.select(state => state.folders),
	);
}
